/**
 * Wires a single HTMLAudioElement to the audioBibleStore.
 *
 * Per-verse clip architecture: the audio element plays one verse clip at a time.
 * On `ended`, we swap `src` to the next verse's URL and play immediately, creating
 * the illusion of a continuous chapter stream.
 *
 * The store's currentPositionSec is a *virtual* time across all verses (sum of
 * durations up to the current clip + the current clip's elapsed time). The <audio>
 * element's own `currentTime` is scoped to the currently-loaded clip only.
 */

import { useEffect, useRef } from 'react'
import { useAudioBibleStore } from '../stores/audioBibleStore'
import { getNextChapter } from '../lib/bibleBooks'

export function useBibleAudioPlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!audioRef.current) {
      const el = new Audio()
      el.preload = 'auto'
      audioRef.current = el
    }
    const audio = audioRef.current

    const unsub = useAudioBibleStore.subscribe((state, prev) => {
      // React to play/pause status transitions
      if (state.status !== prev.status) {
        if (state.status === 'playing' && audio.paused) {
          void audio.play().catch(() => {
            useAudioBibleStore.setState({ status: 'error', errorMessage: 'Playback blocked — tap play' })
          })
        } else if (state.status === 'paused' && !audio.paused) {
          audio.pause()
        }
      }

      // Chapter / voice change → swap manifest
      if (
        state.bookId !== prev.bookId ||
        state.chapter !== prev.chapter ||
        state.voiceId !== prev.voiceId ||
        state.clips.length !== prev.clips.length
      ) {
        // Load the first clip OR the clip containing the current virtual position
        if (state.clips.length > 0 && state.status === 'ready') {
          const targetIdx = findClipIndexForPosition(state.clips, state.cumulativeStarts, state.currentPositionSec)
          loadClip(audio, state.clips[targetIdx].url)
          audio.currentTime = Math.max(0, state.currentPositionSec - state.cumulativeStarts[targetIdx])
        }
      }

      // Speed change
      if (state.speed !== prev.speed) {
        audio.playbackRate = state.speed
      }

      // External seek (when user taps progress bar or jumpToVerse)
      if (
        state.currentPositionSec !== prev.currentPositionSec &&
        Math.abs(state.currentPositionSec - virtualTime(audio, state)) > 0.5
      ) {
        // User-initiated seek — reload the appropriate clip
        const idx = findClipIndexForPosition(state.clips, state.cumulativeStarts, state.currentPositionSec)
        if (state.clips[idx]) {
          loadClip(audio, state.clips[idx].url)
          audio.currentTime = Math.max(0, state.currentPositionSec - state.cumulativeStarts[idx])
          if (state.status === 'playing') {
            void audio.play()
          }
        }
      }
    })

    // Time update handler — drive currentPosition + currentVerse from the clip-scoped time
    const onTimeUpdate = () => {
      const state = useAudioBibleStore.getState()
      if (!state.clips.length) return
      const idx = findClipIndexForUrl(state.clips, audio.src)
      if (idx < 0) return
      const virtual = state.cumulativeStarts[idx] + audio.currentTime
      const clipVerse = state.clips[idx].verse
      const nextVerse = clipVerse !== state.currentVerse ? clipVerse : state.currentVerse
      if (virtual !== state.currentPositionSec || nextVerse !== state.currentVerse) {
        useAudioBibleStore.setState({
          currentPositionSec: virtual,
          currentVerse: nextVerse,
        })
      }
    }

    // Clip ended → advance to next verse OR next chapter
    const onEnded = async () => {
      const state = useAudioBibleStore.getState()
      const idx = findClipIndexForUrl(state.clips, audio.src)
      if (idx < 0) return

      if (idx + 1 < state.clips.length) {
        // Next verse in same chapter
        const nextClip = state.clips[idx + 1]
        loadClip(audio, nextClip.url)
        audio.currentTime = 0
        await audio.play().catch(() => {
          /* blocked */
        })
        useAudioBibleStore.setState({
          currentVerse: nextClip.verse,
          currentPositionSec: state.cumulativeStarts[idx + 1],
        })
      } else if (state.autoNext && state.bookId && state.chapter) {
        // Advance to next chapter
        const next = getNextChapter(state.bookId, state.chapter)
        if (next) {
          await useAudioBibleStore.getState().play(next.bookId, next.chapter, state.translation)
          // the manifest poll will set status=ready, and a separate effect below will auto-play
        } else {
          useAudioBibleStore.setState({ status: 'paused' })
        }
      } else {
        useAudioBibleStore.setState({ status: 'paused' })
      }
    }

    const onError = () => {
      useAudioBibleStore.setState({ status: 'error', errorMessage: 'Audio clip failed to load' })
    }

    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
      unsub()
    }
  }, [])

  // Auto-play when a chapter becomes ready from a play() call
  useEffect(() => {
    const unsub = useAudioBibleStore.subscribe((state, prev) => {
      if (state.status === 'ready' && prev.status === 'loading' && state.clips.length > 0) {
        useAudioBibleStore.setState({ status: 'playing', currentVerse: state.clips[0].verse })
      }
      if (state.status === 'ready' && prev.status === 'synthesizing' && state.clips.length > 0) {
        useAudioBibleStore.setState({ status: 'playing', currentVerse: state.clips[0].verse })
      }
    })
    return unsub
  }, [])
}

function loadClip(audio: HTMLAudioElement, url: string) {
  // Avoid interrupting in-flight network fetch if URL is unchanged
  if (audio.src === url) return
  audio.src = url
  audio.load()
}

function findClipIndexForUrl(clips: { url: string }[], src: string): number {
  // Browsers may absolute-ize relative URLs; compare suffix
  return clips.findIndex((c) => src.endsWith(c.url) || c.url === src)
}

function findClipIndexForPosition(
  clips: { url: string }[],
  cumulative: number[],
  positionSec: number,
): number {
  if (!clips.length) return 0
  for (let i = 0; i < cumulative.length; i++) {
    if (cumulative[i] <= positionSec) {
      if (i + 1 >= cumulative.length || cumulative[i + 1] > positionSec) {
        return i
      }
    }
  }
  return 0
}

function virtualTime(
  audio: HTMLAudioElement,
  state: { clips: { url: string }[]; cumulativeStarts: number[] },
): number {
  const idx = findClipIndexForUrl(state.clips, audio.src)
  if (idx < 0) return 0
  return state.cumulativeStarts[idx] + audio.currentTime
}
